import createInitialState from '@/redux/reducers/uiReducer/lib/createInitialState.js';
import findComposerAttachmentScopeKeyByAttachmentId from '@/redux/reducers/uiReducer/lib/findComposerAttachmentScopeKeyByAttachmentId.js';
import normalizeComposerAttachmentContextBudget from '@/redux/reducers/uiReducer/lib/normalizeComposerAttachmentContextBudget.js';
import normalizeComposerAttachmentEntries from '@/redux/reducers/uiReducer/lib/normalizeComposerAttachmentEntries.js';
import normalizeComposerAttachmentOverflowStrategy from '@/redux/reducers/uiReducer/lib/normalizeComposerAttachmentOverflowStrategy.js';
import resolveComposerScopeKey from '@/redux/reducers/uiReducer/lib/resolveComposerScopeKey.js';
import createPersistedReducer from '@/redux/lib/createPersistedReducer.js';
import buildResetActiveCompanyScopeState from '@/redux/reducers/uiReducer/buildResetActiveCompanyScopeState.js';
import applyPrefsPendingMutations from '@/redux/reducers/uiReducer/lib/applyPrefsPendingMutations.js';
import normalizeThreadSurfacePresentation from '@/redux/reducers/uiReducer/normalizeThreadSurfacePresentation.js';
import normalizePrefsPendingMutation from '@/redux/reducers/uiReducer/lib/normalizePrefsPendingMutation.js';
import normalizeThreadSurfaceSignal from '@/redux/reducers/uiReducer/normalizeThreadSurfaceSignal.js';
import normalizeThreadSurfaceSubmitRequest from '@/redux/reducers/uiReducer/normalizeThreadSurfaceSubmitRequest.js';
import { setDeep } from '@shared/utils';
import ATTACHMENT_OVERFLOW_STRATEGY from '@/lib/composer/ATTACHMENT_OVERFLOW_STRATEGY.js';
import { NON_RUNNING_RUN_STATES } from '@/lib/pieveRunState.js';
import getComposerFocusSurface from '@/lib/getComposerFocusSurface.js';
import newerWorkflowTest from '@/lib/workflows/newerWorkflowTest.js';

// AGENTS: If you find root level helper functions in this file, it is in violation of
// agent instructions. Stop what you are doing and solve immediately according to agent docs.

const initialState = createInitialState();

export default createPersistedReducer(
  createInitialState,
  {
    // Why the project's Pieve chat could not be opened, for the pane to say; the next attempt clears it.
    POST_PIEVE_SLACK_IDENTITY_VERIFICATION_COMPLETE_OPTIMISTIC: (state, payload) => ({ ...state, platformIdentityVerification: null, platformIdentityVerificationRequestId: payload.meta.requestId }),
    POST_PIEVE_SLACK_IDENTITY_VERIFICATION_COMPLETE_SUCCESS: (state, payload) => (state.platformIdentityVerificationRequestId === payload.meta.requestId ? { ...state, platformIdentityVerification: payload } : state),
    POST_PIEVE_SLACK_IDENTITY_VERIFICATION_START_OPTIMISTIC: (state) => ({ ...state, platformIdentityVerification: null, platformIdentityVerificationRequestId: null }),
    POST_PIEVE_PLATFORM_LINK_OPTIMISTIC: (state) => ({ ...state, platformIdentityVerification: null, platformIdentityVerificationRequestId: null }),
    SET_CURRENT_CREDENTIAL_LOAD_REQUEST: (state, payload) => {
      return { ...state, credentialImpactResponse: null, credentialImpactLoadRequestId: payload.loadRequestId };
    },
    CLEAR_CURRENT_CREDENTIAL: (state) => {
      return { ...state, credentialImpactResponse: null, credentialImpactLoadRequestId: null };
    },
    GET_CREDENTIAL_IMPACT_SUCCESS: (state, payload) => {
      if (!payload?.meta?.loadRequestId || payload.meta.loadRequestId !== state.credentialImpactLoadRequestId) {
        return state;
      }
      return { ...state, credentialImpactResponse: payload };
    },
    GET_CREDENTIAL_IMPACT_FAIL: (state, payload) => {
      if (!payload?.meta?.loadRequestId || payload.meta.loadRequestId !== state.credentialImpactLoadRequestId) {
        return state;
      }
      return { ...state, credentialImpactResponse: payload };
    },
    RESET_PIEVE_ACTIVE_COMPANY_SCOPE: (state) => {
      return buildResetActiveCompanyScopeState({
        state,
        initialState
      });
    },

    uiSetWorkspaceCompanyListLoading: (state, payload) => {
      return {
        ...state,
        isWorkspaceCompanyListLoading: payload === true
      };
    },

    uiSetWorkspaceCompanyListError: (state, payload) => {
      return {
        ...state,
        workspaceCompanyListError: String(payload || '')
      };
    },

    uiSetWorkspaceSwitchingCompanyId: (state, payload) => {
      return {
        ...state,
        workspaceSwitchingCompanyId: payload ? String(payload) : null
      };
    },

    uiSetWorkspaceSidebarCollapsed: (state, payload) => {
      return {
        ...state,
        workspaceSidebarCollapsed: payload === true
      };
    },

    uiSetWorkflowSurfaceSingleColumnView: (state, payload) => {
      return {
        ...state,
        workflowSurfaceSingleColumnView: payload === 'workflow' ? 'workflow' : 'chat'
      };
    },

    uiSetDesktopWorkspaceSidebarCollapsed: (state, payload) => {
      return {
        ...state,
        desktopWorkspaceSidebarCollapsed: payload === true
      };
    },

    uiSetDesktopSidebarPeek: (state, payload) => ({ ...state, desktopSidebarPeek: payload }),

    // --- Composer singletons ---

    uiSetVoiceRecordingState: (state, payload) => {
      if (!payload || typeof payload !== 'object') {
        return state;
      }
      return {
        ...state,
        voiceRecording: {
          ...state.voiceRecording,
          ...payload
        }
      };
    },

    uiIncrementVoiceRecordingStartRequest: (state, payload) => {
      return {
        ...state,
        voiceRecording: {
          ...state.voiceRecording,
          startRequestId: (state.voiceRecording?.startRequestId || 0) + 1,
          requestScopeKey: String(payload?.scopeKey || '').trim()
        }
      };
    },

    uiIncrementVoiceRecordingStopRequest: (state, payload) => {
      return {
        ...state,
        voiceRecording: {
          ...state.voiceRecording,
          stopRequestId: (state.voiceRecording?.stopRequestId || 0) + 1,
          stopSubmitAfter: payload?.submitAfter === true,
          requestScopeKey: String(payload?.scopeKey || '').trim()
        }
      };
    },

    uiIncrementComposerSendRequest: (state, payload) => {
      return {
        ...state,
        composerSendRequestId: (state.composerSendRequestId || 0) + 1,
        composerSendRequestScopeKey: String(payload?.scopeKey || '').trim()
      };
    },

    uiIncrementComposerSubmitted: (state, payload) => {
      return {
        ...state,
        composerSubmittedId: (state.composerSubmittedId || 0) + 1,
        composerSubmittedScopeKey: String(payload?.scopeKey || '').trim()
      };
    },

    // --- End new singletons ---

    uiSetComposerAttachmentOverflowStrategy: (state, payload) => {
      const scopeKey = resolveComposerScopeKey({
        scopeKey: payload?.scopeKey || null
      });
      const nextStrategy = normalizeComposerAttachmentOverflowStrategy(payload?.strategy);
      const previousStrategy = state?.composerAttachmentOverflowStrategyByScope?.[scopeKey] || ATTACHMENT_OVERFLOW_STRATEGY.truncate;

      if (previousStrategy === nextStrategy) {
        return state;
      }

      return {
        ...state,
        composerAttachmentOverflowStrategyByScope: {
          ...(state?.composerAttachmentOverflowStrategyByScope || {}),
          [scopeKey]: nextStrategy
        }
      };
    },

    uiSetComposerAttachmentsForScope: (state, payload) => {
      const scopeKey = resolveComposerScopeKey({
        scopeKey: payload?.scopeKey || null
      });
      const nextItems = normalizeComposerAttachmentEntries(payload?.items);
      const nextAttachmentsByScope = {
        ...(state?.composerAttachmentsByScope || {})
      };

      if (nextItems.length > 0) {
        nextAttachmentsByScope[scopeKey] = nextItems;
      } else {
        delete nextAttachmentsByScope[scopeKey];
      }

      return {
        ...state,
        composerAttachmentsByScope: nextAttachmentsByScope
      };
    },

    uiClearComposerAttachmentsForScope: (state, payload) => {
      const scopeKey = resolveComposerScopeKey({
        scopeKey: payload?.scopeKey || null
      });

      if (!state?.composerAttachmentsByScope?.[scopeKey]) {
        return state;
      }

      const nextAttachmentsByScope = {
        ...(state?.composerAttachmentsByScope || {})
      };
      delete nextAttachmentsByScope[scopeKey];

      return {
        ...state,
        composerAttachmentsByScope: nextAttachmentsByScope
      };
    },

    uiRemoveComposerAttachment: (state, payload) => {
      const attachmentsByScope = state?.composerAttachmentsByScope || {};
      const scopeKey = resolveComposerScopeKey({
        scopeKey:
          payload?.scopeKey ||
          findComposerAttachmentScopeKeyByAttachmentId({
            attachmentId: payload?.attachmentId,
            attachmentsByScope
          })
      });
      const normalizedAttachmentId = String(payload?.attachmentId || '').trim();
      const previousItems = Array.isArray(attachmentsByScope?.[scopeKey]) ? attachmentsByScope[scopeKey] : [];

      if (!normalizedAttachmentId || previousItems.length < 1) {
        return state;
      }

      const nextItems = previousItems.filter((item) => {
        return item?.id !== normalizedAttachmentId;
      });

      if (nextItems.length === previousItems.length) {
        return state;
      }

      const nextAttachmentsByScope = {
        ...attachmentsByScope
      };

      if (nextItems.length > 0) {
        nextAttachmentsByScope[scopeKey] = nextItems;
      } else {
        delete nextAttachmentsByScope[scopeKey];
      }

      return {
        ...state,
        composerAttachmentsByScope: nextAttachmentsByScope
      };
    },

    uiRetryComposerAttachment: (state, payload) => {
      const attachmentsByScope = state?.composerAttachmentsByScope || {};
      const scopeKey = resolveComposerScopeKey({
        scopeKey:
          payload?.scopeKey ||
          findComposerAttachmentScopeKeyByAttachmentId({
            attachmentId: payload?.attachmentId,
            attachmentsByScope
          })
      });
      const normalizedAttachmentId = String(payload?.attachmentId || '').trim();
      const previousItems = Array.isArray(attachmentsByScope?.[scopeKey]) ? attachmentsByScope[scopeKey] : [];

      if (!normalizedAttachmentId || previousItems.length < 1) {
        return state;
      }

      let didChange = false;
      const nextItems = previousItems.map((item) => {
        if (item?.id !== normalizedAttachmentId) {
          return item;
        }

        didChange = true;
        return {
          ...item,
          error: '',
          status: 'uploading',
          uploadProgressPercent: 0,
          uploadResult: null
        };
      });

      if (!didChange) {
        return state;
      }

      return {
        ...state,
        composerAttachmentsByScope: {
          ...attachmentsByScope,
          [scopeKey]: nextItems
        }
      };
    },

    uiUpdateComposerAttachmentUploadState: (state, payload) => {
      const attachmentsByScope = state?.composerAttachmentsByScope || {};
      const scopeKey = findComposerAttachmentScopeKeyByAttachmentId({
        attachmentId: payload?.attachmentId,
        attachmentsByScope
      });

      if (!scopeKey) {
        return state;
      }

      const normalizedAttachmentId = String(payload?.attachmentId || '').trim();
      const previousItems = Array.isArray(attachmentsByScope?.[scopeKey]) ? attachmentsByScope[scopeKey] : [];
      let didChange = false;
      const nextItems = previousItems.map((item) => {
        if (item?.id !== normalizedAttachmentId) {
          return item;
        }

        didChange = true;
        const update = {};

        if (payload?.status) {
          update.status = payload.status;
        }

        if (payload?.uploadProgressPercent !== null && payload?.uploadProgressPercent !== undefined) {
          update.uploadProgressPercent = payload.uploadProgressPercent;
        }

        if (payload?.uploadResult !== undefined) {
          update.uploadResult = payload.uploadResult;
        }

        if (payload?.error !== undefined) {
          update.error = payload.error || '';
        }

        return {
          ...item,
          ...update
        };
      });

      if (!didChange) {
        return state;
      }

      return {
        ...state,
        composerAttachmentsByScope: {
          ...attachmentsByScope,
          [scopeKey]: nextItems
        }
      };
    },

    uiSetComposerAttachmentContextBudget: (state, payload) => {
      const scopeKey = resolveComposerScopeKey({
        scopeKey: payload?.scopeKey || null
      });
      const nextBudget = normalizeComposerAttachmentContextBudget(payload?.attachmentContextBudget);
      const nextBudgetByScope = {
        ...(state?.composerAttachmentContextBudgetByScope || {})
      };

      if (nextBudget) {
        nextBudgetByScope[scopeKey] = nextBudget;
      } else {
        delete nextBudgetByScope[scopeKey];
      }

      return {
        ...state,
        composerAttachmentContextBudgetByScope: nextBudgetByScope
      };
    },

    uiClearComposerAttachmentSkillSuggestionRequest: (state, payload) => {
      const scopeKey = resolveComposerScopeKey({
        scopeKey: payload?.scopeKey || null
      });
      const previousRequest = state?.composerAttachmentSkillSuggestionRequestByScope?.[scopeKey] || null;

      if (!previousRequest) {
        return state;
      }

      const normalizedRequestId = Number(payload?.requestId || 0);

      if (normalizedRequestId > 0 && Number(previousRequest?.requestId || 0) !== normalizedRequestId) {
        return state;
      }

      const nextRequestsByScope = {
        ...(state?.composerAttachmentSkillSuggestionRequestByScope || {})
      };
      delete nextRequestsByScope[scopeKey];

      return {
        ...state,
        composerAttachmentSkillSuggestionRequestByScope: nextRequestsByScope
      };
    },

    uiSetRequestLoader: (state, payload) => {
      const normalizedKey = String(payload?.key || '').trim();

      if (!normalizedKey) {
        return state;
      }

      const nextEntry = {
        ...(state?.requestLoadersByKey?.[normalizedKey] || {}),
        isLoading: payload?.isLoading === true
      };

      if (payload?.loadStart) {
        nextEntry.loadStart = String(payload.loadStart);
      }

      if (payload?.loadEnd) {
        nextEntry.loadEnd = String(payload.loadEnd);
      }

      return {
        ...state,
        requestLoadersByKey: {
          ...(state?.requestLoadersByKey || {}),
          [normalizedKey]: nextEntry
        }
      };
    },

    GET_THREAD_CONTEXT_PREVIEW_SUCCESS: (state, payload) => {
      return {
        ...state,
        threadContextPreview: payload && typeof payload === 'object' ? payload : null
      };
    },

    GET_PIEVE_WEBSITE_HANDOFF_SIGNUP_SUBTITLE_SUCCESS: (state, payload) => {
      return {
        ...state,
        websiteSignupSubtitles: Array.isArray(payload?.data) ? payload.data : []
      };
    },

    GET_THREAD_CONTEXT_PREVIEW_FAIL: (state, payload) => {
      return {
        ...state,
        threadContextPreview: payload && typeof payload === 'object' ? payload : null
      };
    },

    GET_PUBLIC_THREAD_SHARE_SUCCESS: (state, payload) => {
      return {
        ...state,
        activeThreadId: String(payload?.data?.threadId || '').trim() || null
      };
    },

    GET_PUBLIC_THREAD_SHARE_FAIL: (state) => {
      return {
        ...state,
        activeThreadId: null
      };
    },

    uiClearThreadContextPreview: (state) => {
      if (state?.threadContextPreview === null) {
        return state;
      }

      return {
        ...state,
        threadContextPreview: null
      };
    },

    pieveClearPublicThread: (state) => {
      return {
        ...state,
        activeThreadId: null
      };
    },

    uiSetActiveAutomationsPane: (state, payload) => {
      const normalizedValue = String(payload || '')
        .trim()
        .toLowerCase();
      const nextPane = normalizedValue === 'triggers' ? 'triggers' : normalizedValue === 'workflows' ? 'workflows' : 'schedules';

      if (state?.automations?.activePane === nextPane) {
        return state;
      }

      return {
        ...state,
        automations: {
          ...(state?.automations || {}),
          activePane: nextPane
        }
      };
    },

    pieveSetThreadRunState: (state, payload) => {
      const threadId = String(payload?.threadId || '').trim();

      if (!threadId) {
        return state;
      }

      const normalizedRunState = String(payload?.state || '')
        .trim()
        .toLowerCase();
      const isTerminalRunState = NON_RUNNING_RUN_STATES.has(normalizedRunState);
      const hasChecklistOverride = Object.prototype.hasOwnProperty.call(state?.activeChecklistByThreadId || {}, threadId);
      let nextActiveChecklistByThreadId = state?.activeChecklistByThreadId || {};
      let activeChecklistChanged = false;

      if (isTerminalRunState) {
        if (!hasChecklistOverride || nextActiveChecklistByThreadId[threadId] !== null) {
          nextActiveChecklistByThreadId = {
            ...(state?.activeChecklistByThreadId || {}),
            [threadId]: null
          };
          activeChecklistChanged = true;
        }
      } else if (hasChecklistOverride) {
        nextActiveChecklistByThreadId = {
          ...(state?.activeChecklistByThreadId || {})
        };
        delete nextActiveChecklistByThreadId[threadId];
        activeChecklistChanged = true;
      }

      const currentPresentation = state?.threadSurfacePresentation || null;
      const shouldResetPresentationQueue = isTerminalRunState && currentPresentation && ((currentPresentation.queueVisibleCount || 0) > 0 || currentPresentation.queueEnteringKey || currentPresentation.queueHiddenAnchorKey);

      if (!activeChecklistChanged && !shouldResetPresentationQueue) {
        return state;
      }

      return {
        ...state,
        activeChecklistByThreadId: nextActiveChecklistByThreadId,
        ...(shouldResetPresentationQueue
          ? {
              threadSurfacePresentation: {
                ...currentPresentation,
                queueVisibleCount: 0,
                queueEnteringKey: '',
                queueHiddenAnchorKey: ''
              }
            }
          : {})
      };
    },

    GET_DEV_OVERRIDES_SUCCESS: (state, payload) => {
      return {
        ...state,
        devOverrides: {
          groups: Array.isArray(payload?.data?.groups) ? payload.data.groups : [],
          overrides: payload?.data?.overrides && typeof payload.data.overrides === 'object' ? payload.data.overrides : {}
        }
      };
    },

    PUT_DEV_OVERRIDE_OPTIMISTIC: (state, payload) => {
      const groupKey = String(payload?.groupKey || '').trim();

      if (!groupKey || !state?.devOverrides) {
        return state;
      }

      const nextOverrides = { ...(state.devOverrides.overrides || {}) };
      const value = String(payload?.value || '').trim();

      if (value) {
        nextOverrides[groupKey] = value;
      } else {
        delete nextOverrides[groupKey];
      }

      return {
        ...state,
        devOverrides: {
          ...state.devOverrides,
          overrides: nextOverrides
        }
      };
    },

    uiSetEventKey: (state, payload) => {
      const key = String(payload?.key || '').trim();

      if (!key) {
        return state;
      }

      return {
        ...state,
        eventKeys: {
          ...(state?.eventKeys || {}),
          [key]: payload?.timestamp || Date.now()
        }
      };
    },

    uiSetThreadOlderEventsState: (state, payload) => {
      const threadId = String(payload?.threadId || '').trim();

      if (!threadId) {
        return state;
      }

      const previousEntry = state?.olderThreadEventsByThreadId?.[threadId] || {};
      const nextEntry = {
        ...previousEntry
      };
      let changed = false;

      if (Object.prototype.hasOwnProperty.call(payload || {}, 'hasOlder')) {
        const nextHasOlder = payload?.hasOlder === true;

        if (previousEntry?.hasOlder !== nextHasOlder) {
          nextEntry.hasOlder = nextHasOlder;
          changed = true;
        }
      }

      if (Object.prototype.hasOwnProperty.call(payload || {}, 'loadingOlder')) {
        const nextLoadingOlder = payload?.loadingOlder === true;

        if (previousEntry?.loadingOlder !== nextLoadingOlder) {
          nextEntry.loadingOlder = nextLoadingOlder;
          changed = true;
        }
      }

      if (Object.prototype.hasOwnProperty.call(payload || {}, 'knownFloorSeq')) {
        const rawKnownFloorSeq = payload?.knownFloorSeq;
        const nextKnownFloorSeq = Number.isFinite(Number(rawKnownFloorSeq)) ? Number(rawKnownFloorSeq) : null;
        const previousKnownFloorSeq = Number.isFinite(Number(previousEntry?.knownFloorSeq)) ? Number(previousEntry.knownFloorSeq) : null;

        if (previousKnownFloorSeq !== nextKnownFloorSeq) {
          nextEntry.knownFloorSeq = nextKnownFloorSeq;
          changed = true;
        }
      }

      if (!changed) {
        return state;
      }

      return {
        ...state,
        olderThreadEventsByThreadId: {
          ...(state?.olderThreadEventsByThreadId || {}),
          [threadId]: nextEntry
        }
      };
    },

    uiClearThreadOlderEventsState: (state, payload) => {
      const threadId = String(payload || '').trim();

      if (!threadId || !state?.olderThreadEventsByThreadId?.[threadId]) {
        return state;
      }

      const nextOlderThreadEventsByThreadId = {
        ...(state?.olderThreadEventsByThreadId || {})
      };
      delete nextOlderThreadEventsByThreadId[threadId];

      return {
        ...state,
        olderThreadEventsByThreadId: nextOlderThreadEventsByThreadId
      };
    },

    uiSetPref: (state, payload) => {
      let nextPrefs = {
        ...(state?.prefs || {})
      };
      let nextServerPayload = {
        ...(state?.prefsServerPayload || {})
      };

      for (const [path, value] of Object.entries(payload || {})) {
        const normalizedPath = String(path || '').trim();

        if (!normalizedPath) {
          continue;
        }

        nextPrefs = setDeep(nextPrefs, normalizedPath, value);
        nextServerPayload = setDeep(nextServerPayload, normalizedPath, value);
      }

      return {
        ...state,
        prefs: nextPrefs,
        prefsServerPayload: nextServerPayload
      };
    },

    uiResetPrefs: (state) => {
      return {
        ...state,
        prefs: {},
        prefsPendingMutations: [],
        prefsRequestStatus: 'idle',
        prefsServerPayload: {}
      };
    },

    uiSetSettingsLoading: (state, payload) => {
      return {
        ...state,
        settings: {
          ...(state?.settings || initialState.settings),
          isLoading: payload === true
        }
      };
    },

    uiSetSettingsSaving: (state, payload) => {
      return {
        ...state,
        settings: {
          ...(state?.settings || initialState.settings),
          isSaving: payload === true
        }
      };
    },

    uiSetSettingsError: (state, payload) => {
      return {
        ...state,
        settings: {
          ...(state?.settings || initialState.settings),
          error: String(payload || '')
        }
      };
    },

    uiSetSettingsResolved: (state, payload) => {
      return {
        ...state,
        settings: {
          ...(state?.settings || initialState.settings),
          hasResolvedInitialSettings: payload === true
        }
      };
    },

    uiSetPendingSkillDraftItem: (state, payload) => {
      return {
        ...state,
        pendingSkillDraftItem: payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : null
      };
    },

    uiSetMainThreadTerminalOpen: (state, payload) => {
      const normalizedThreadId = String(payload?.threadId || '').trim();

      if (!normalizedThreadId) {
        return state;
      }

      const nextOpen = payload?.open === true;
      const previousOpen = state?.mainThreadTerminalOpenByThreadId?.[normalizedThreadId];

      if (previousOpen === nextOpen) {
        return state;
      }

      return {
        ...state,
        mainThreadTerminalOpenByThreadId: {
          ...(state?.mainThreadTerminalOpenByThreadId || {}),
          [normalizedThreadId]: nextOpen
        }
      };
    },

    uiSetMainThreadSubagentPaneHiddenScope: (state, payload) => {
      const normalizedScopeKey = String(payload?.scopeKey || '').trim();

      if (!normalizedScopeKey) {
        return state;
      }

      const hiddenContextIds = Array.isArray(payload?.hiddenContextIds) ? payload.hiddenContextIds : [];
      const nextScopeState = hiddenContextIds.reduce((accumulator, contextId) => {
        const normalizedContextId = String(contextId || '').trim();

        if (normalizedContextId) {
          accumulator[normalizedContextId] = true;
        }

        return accumulator;
      }, {});

      return {
        ...state,
        mainThreadSubagentPaneHiddenByScope: {
          ...(state?.mainThreadSubagentPaneHiddenByScope || {}),
          [normalizedScopeKey]: nextScopeState
        }
      };
    },

    uiSetMainThreadSubagentPaneHidden: (state, payload) => {
      const normalizedScopeKey = String(payload?.scopeKey || '').trim();
      const normalizedContextId = String(payload?.contextId || '').trim();

      if (!normalizedScopeKey || !normalizedContextId) {
        return state;
      }

      const previousScopeState = state?.mainThreadSubagentPaneHiddenByScope?.[normalizedScopeKey] || {};
      const nextHidden = payload?.hidden === true;

      if (previousScopeState?.[normalizedContextId] === nextHidden) {
        return state;
      }

      const nextScopeState = {
        ...previousScopeState
      };

      if (nextHidden) {
        nextScopeState[normalizedContextId] = true;
      } else {
        delete nextScopeState[normalizedContextId];
      }

      const nextByScope = {
        ...(state?.mainThreadSubagentPaneHiddenByScope || {})
      };

      if (Object.keys(nextScopeState).length > 0) {
        nextByScope[normalizedScopeKey] = nextScopeState;
      } else {
        delete nextByScope[normalizedScopeKey];
      }

      return {
        ...state,
        mainThreadSubagentPaneHiddenByScope: nextByScope
      };
    },

    uiSetMainThreadHydrationSettled: (state, payload) => {
      const nextSettled = payload === true;

      if (state?.mainThreadHydrationSettled === nextSettled) {
        return state;
      }

      return {
        ...state,
        mainThreadHydrationSettled: nextSettled
      };
    },

    uiSetThreadSurfaceRouteHydrationState: (state, payload) => {
      const nextThreadId = payload?.threadId ? String(payload.threadId).trim() : null;
      const nextRequestId = Number(payload?.requestId) || 0;
      const nextLoading = payload?.loading === true;
      const currentState = state?.threadSurfaceRouteHydration || null;
      const currentRequestId = Number(currentState?.requestId || 0);

      if (nextRequestId < currentRequestId) {
        return state;
      }

      if (currentState?.threadId === nextThreadId && currentRequestId === nextRequestId && currentState?.loading === nextLoading) {
        return state;
      }

      return {
        ...state,
        threadSurfaceRouteHydration: {
          threadId: nextThreadId,
          requestId: nextRequestId,
          loading: nextLoading
        }
      };
    },

    uiSetThreadSurfacePresentation: (state, payload) => {
      const nextThreadSurfacePresentation = normalizeThreadSurfacePresentation(payload);

      if (!nextThreadSurfacePresentation) {
        return state;
      }

      const currentPresentation = state?.threadSurfacePresentation;

      if (currentPresentation?.mode === nextThreadSurfacePresentation.mode && currentPresentation?.modeState === nextThreadSurfacePresentation.modeState && currentPresentation?.scrollOwner === nextThreadSurfacePresentation.scrollOwner) {
        return state;
      }

      return {
        ...state,
        threadSurfacePresentation: {
          anchorPresentationKey: currentPresentation?.anchorPresentationKey || '',
          spacerBudget: currentPresentation?.spacerBudget || 0,
          hydrationRestTarget: currentPresentation?.hydrationRestTarget || null,
          queueVisibleCount: currentPresentation?.queueVisibleCount || 0,
          queueEnteringKey: currentPresentation?.queueEnteringKey || '',
          queueHiddenAnchorKey: currentPresentation?.queueHiddenAnchorKey || '',
          isHydrationVisible: currentPresentation?.isHydrationVisible === true,
          ...nextThreadSurfacePresentation
        }
      };
    },

    uiClearThreadSurfacePresentation: (state) => {
      if (!state?.threadSurfacePresentation) {
        return state;
      }

      return {
        ...state,
        threadSurfacePresentation: null
      };
    },

    uiEnqueueThreadSurfaceSignal: (state, payload) => {
      const normalizedSignal = normalizeThreadSurfaceSignal(payload);

      if (!normalizedSignal) {
        return state;
      }

      const nextSignalId = Number(state?.threadSurfaceSignalQueue?.[state.threadSurfaceSignalQueue.length - 1]?.signalId || 0) + 1;

      return {
        ...state,
        threadSurfaceSignalQueue: [
          ...(state?.threadSurfaceSignalQueue || []),
          {
            signalId: nextSignalId,
            ...normalizedSignal
          }
        ].slice(-50)
      };
    },

    uiAcknowledgeThreadSurfaceSignal: (state, payload) => {
      const normalizedSignalId = Number(payload?.signalId || 0);

      if (!normalizedSignalId || !Array.isArray(state?.threadSurfaceSignalQueue) || state.threadSurfaceSignalQueue.length === 0) {
        return state;
      }

      const nextSignalQueue = state.threadSurfaceSignalQueue.filter((signal) => {
        return Number(signal?.signalId || 0) !== normalizedSignalId;
      });

      if (nextSignalQueue.length === state.threadSurfaceSignalQueue.length) {
        return state;
      }

      return {
        ...state,
        threadSurfaceSignalQueue: nextSignalQueue
      };
    },

    uiClearThreadSurfaceSignals: (state) => {
      if (!Array.isArray(state?.threadSurfaceSignalQueue) || state.threadSurfaceSignalQueue.length === 0) {
        return state;
      }

      return {
        ...state,
        threadSurfaceSignalQueue: []
      };
    },

    uiSetThreadSurfaceSubmitRequest: (state, payload) => {
      const normalizedRequest = normalizeThreadSurfaceSubmitRequest(payload);

      if (!normalizedRequest) {
        return state;
      }

      const currentRequest = state?.threadSurfaceSubmitRequest || null;

      if (currentRequest?.threadId === normalizedRequest.threadId && currentRequest?.submissionToken === normalizedRequest.submissionToken && currentRequest?.source === normalizedRequest.source && currentRequest?.sourceSurface === normalizedRequest.sourceSurface && currentRequest?.startedAt === normalizedRequest.startedAt) {
        return state;
      }

      return {
        ...state,
        threadSurfaceSubmitRequest: normalizedRequest
      };
    },

    uiClearThreadSurfaceSubmitRequest: (state, payload) => {
      const normalizedSubmissionToken = String(payload?.submissionToken || '').trim();
      const currentRequest = state?.threadSurfaceSubmitRequest || null;

      if (!currentRequest) {
        return state;
      }

      if (normalizedSubmissionToken && currentRequest.submissionToken !== normalizedSubmissionToken) {
        return state;
      }

      return {
        ...state,
        threadSurfaceSubmitRequest: null
      };
    },

    /* Names the thread the admin thread viewer is reading. buildHeaders turns this into the
       x-admin-thread-view request header; nothing else in the renderer reads it. */
    uiSetAdminThreadViewId: (state, payload) => {
      const normalizedThreadId = String(payload || '').trim() || null;

      if (state?.adminThreadViewId === normalizedThreadId) {
        return state;
      }

      return {
        ...state,
        adminThreadViewId: normalizedThreadId
      };
    },

    uiSetActiveThreadId: (state, payload) => {
      const normalizedThreadId = String(payload || '').trim() || null;

      if (state?.activeThreadId === normalizedThreadId) {
        return state;
      }

      return {
        ...state,
        activeThreadId: normalizedThreadId
      };
    },

    pieveSetSelectedThreadId: (state, payload) => {
      const normalizedThreadId = String(payload || '').trim() || null;

      if (state?.selectedThreadId === normalizedThreadId) {
        return state;
      }

      return {
        ...state,
        selectedThreadId: normalizedThreadId
      };
    },

    pieveArchiveThreadOptimistic: (state, payload) => {
      const normalizedThreadId = String(payload?.threadId || payload?.resourceId || '').trim() || null;

      if (!normalizedThreadId || state?.selectedThreadId !== normalizedThreadId) {
        return state;
      }

      return {
        ...state,
        selectedThreadId: null
      };
    },

    pieveRestoreArchivedThread: (state, payload) => {
      const normalizedThreadId = String(payload?.selectedThreadIdBefore || '').trim() || null;

      if (!normalizedThreadId || state?.selectedThreadId === normalizedThreadId) {
        return state;
      }

      return {
        ...state,
        selectedThreadId: normalizedThreadId
      };
    },

    uiSetIsAutoPendingNewThread: (state, payload) => {
      return {
        ...state,
        isAutoPendingNewThread: payload?.value === true
      };
    },

    uiRequestComposerFocus: (state, payload) => {
      const surface = getComposerFocusSurface(payload?.surface);
      const previousRequestId = Number(state?.composerFocusRequestBySurface?.[surface] || 0);

      return {
        ...state,
        composerFocusRequestBySurface: {
          ...(state?.composerFocusRequestBySurface || initialState.composerFocusRequestBySurface),
          [surface]: previousRequestId + 1
        }
      };
    },

    uiSetCrossThreadEmptyStateTransition: (state, payload) => {
      const normalizedTargetThreadId = String(payload?.targetThreadId || '').trim();
      const normalizedToken = String(payload?.token || '').trim();

      return {
        ...state,
        crossThreadEmptyStateTransition:
          normalizedTargetThreadId && normalizedToken
            ? {
                targetThreadId: normalizedTargetThreadId,
                token: normalizedToken,
                sourceViewTransitionArmed: payload?.sourceViewTransitionArmed === true
              }
            : null
      };
    },

    uiStartEmptyStateSubmission: (state, payload) => {
      const normalizedToken = String(payload?.token || '').trim();
      const normalizedSurface = getComposerFocusSurface(payload?.sourceSurface);

      if (!normalizedToken) {
        return state;
      }

      return {
        ...state,
        emptyStateSubmission: {
          token: normalizedToken,
          sourceSurface: normalizedSurface,
          startedAt: String(payload?.startedAt || '').trim() || new Date().toISOString(),
          targetThreadId: null
        }
      };
    },

    uiSetEmptyStateSubmissionTargetThreadId: (state, payload) => {
      const normalizedToken = String(payload?.token || '').trim();
      const normalizedThreadId = String(payload?.threadId || '').trim();

      if (!normalizedToken || !normalizedThreadId) {
        return state;
      }

      if (String(state?.emptyStateSubmission?.token || '').trim() !== normalizedToken) {
        return state;
      }

      if (String(state?.emptyStateSubmission?.targetThreadId || '').trim() === normalizedThreadId) {
        return state;
      }

      return {
        ...state,
        emptyStateSubmission: {
          ...(state?.emptyStateSubmission || {}),
          targetThreadId: normalizedThreadId
        }
      };
    },

    uiClearEmptyStateSubmission: (state, payload) => {
      const normalizedToken = String(payload?.token || '').trim();

      if (!state?.emptyStateSubmission) {
        return state;
      }

      if (normalizedToken && String(state.emptyStateSubmission.token || '').trim() !== normalizedToken) {
        return state;
      }

      return {
        ...state,
        emptyStateSubmission: null
      };
    },

    uiIncrementSettingsArchivedThreadsRefreshKey: (state) => {
      return {
        ...state,
        settings: {
          ...(state?.settings || initialState.settings),
          archivedThreadsRefreshKey: Number(state?.settings?.archivedThreadsRefreshKey || 0) + 1
        }
      };
    },

    uiRequestSkillDraftThreadStart: (state, payload) => {
      const normalizedSkillId = String(payload?.skillId || '').trim();

      if (!normalizedSkillId) {
        return state;
      }

      return {
        ...state,
        skillDraftThreadStartRequest: {
          requestId: Number(state?.skillDraftThreadStartRequest?.requestId || 0) + 1,
          skillId: normalizedSkillId,
          mode:
            String(payload?.mode || 'try')
              .trim()
              .toLowerCase() === 'edit'
              ? 'edit'
              : 'try'
        }
      };
    },

    uiClearSkillDraftThreadStartRequest: (state) => {
      if (!state?.skillDraftThreadStartRequest?.requestId && !state?.skillDraftThreadStartRequest?.skillId && !state?.skillDraftThreadStartRequest?.mode) {
        return state;
      }

      return {
        ...state,
        skillDraftThreadStartRequest: {
          requestId: Number(state?.skillDraftThreadStartRequest?.requestId || 0),
          skillId: null,
          mode: null
        }
      };
    },

    uiSetModalOpen: (state, payload) => {
      const modalKey = String(payload?.key || '').trim();
      const { key: _ignoredKey, ...modalPayload } = payload || {};

      if (!modalKey) {
        return state;
      }

      const nextModalState = {
        ...(state?.modals?.[modalKey] || {}),
        ...modalPayload,
        open: payload?.open === true
      };

      return {
        ...state,
        modals: {
          ...(state?.modals || {}),
          [modalKey]: nextModalState
        }
      };
    },

    uiRequestSkillRemove: (state, payload) => {
      const skillId = String(payload?.skillId || '').trim();
      return { ...state, skillRemoveRequest: skillId ? { skillId, requestId: (state?.skillRemoveRequest?.requestId || 0) + 1 } : null };
    },

    uiConsumeSkillRemoveRequest: (state) => {
      return state?.skillRemoveRequest ? { ...state, skillRemoveRequest: null } : state;
    },

    uiOpenContextMenu: (state, payload) => {
      return { ...state, activeContextMenu: payload ? { menuIds: payload.menuIds, propsByMenuId: payload.propsByMenuId, x: payload.x, y: payload.y } : null };
    },

    uiCloseContextMenu: (state) => {
      return state.activeContextMenu ? { ...state, activeContextMenu: null } : state;
    },

    uiSetAutomationScheduleRunSelectionContext: (state, payload) => {
      return {
        ...state,
        automations: {
          ...(state?.automations || initialState.automations),
          scheduleRunSelectionContext: {
            scheduleId: payload?.scheduleId ? String(payload.scheduleId).trim() : null,
            runThreadId: payload?.runThreadId ? String(payload.runThreadId).trim() : null
          }
        }
      };
    },

    uiSetAutomationTriggerSelection: (state, payload) => {
      const nextTriggerId = payload?.triggerId ? String(payload.triggerId).trim() : null;

      if (state?.automations?.triggerSelectionId === nextTriggerId) {
        return state;
      }

      return {
        ...state,
        automations: {
          ...(state?.automations || initialState.automations),
          triggerSelectionId: nextTriggerId
        }
      };
    },

    uiSetAutomationTriggerRunSelectionContext: (state, payload) => {
      return {
        ...state,
        automations: {
          ...(state?.automations || initialState.automations),
          triggerRunSelectionContext: {
            triggerId: payload?.triggerId ? String(payload.triggerId).trim() : null,
            runThreadId: payload?.runThreadId ? String(payload.runThreadId).trim() : null
          }
        }
      };
    },

    uiSetAutomationWorkflowRunLogsModal: (state, payload) => {
      return {
        ...state,
        modals: {
          ...(state?.modals || {}),
          automationWorkflowRunLogs: {
            ...(state?.modals?.automationWorkflowRunLogs || {}),
            open: payload?.open === true,
            workflowId: payload?.workflowId ? String(payload.workflowId).trim() : null,
            workflowRunId: payload?.workflowRunId ? String(payload.workflowRunId).trim() : null,
            isTestSetup: payload?.isTestSetup === true,
            searchMatch: payload?.searchMatch || null,
            // Every open shows the stored test as it is: no edits, no notice.
            testChoices: null,
            testBaseRevision: null,
            testNotice: null
          }
        }
      };
    },

    uiSetWorkflowTestResultsModal: (state, payload) => {
      return { ...state, modals: { ...(state?.modals || {}), workflowTestResults: { open: payload?.open === true, workflowId: payload?.workflowId || null, workflowRunId: payload?.workflowRunId || null } } };
    },

    // The base is where the edits started: kept while they go on, taken from the stored test when they begin.
    uiSetWorkflowTestChoices: (state, payload) => {
      const modal = state?.modals?.automationWorkflowRunLogs || {};

      return {
        ...state,
        modals: {
          ...(state?.modals || {}),
          automationWorkflowRunLogs: { ...modal, testChoices: payload?.choices || null, testBaseRevision: modal.testChoices !== null && modal.testChoices !== undefined ? modal.testBaseRevision : (payload?.baseRevision ?? 0), testNotice: null }
        }
      };
    },

    /* Which thread's draft read last failed, so the step list can say so instead of claiming the workflow has no
       steps. A read for that thread landing clears it; a planning thread's draft not existing yet fails here too,
       which the step list never shows — it reads this only in build mode, where a draft always exists. */
    GET_THREAD_WORKFLOW_DRAFT_SUCCESS: (state, payload) => {
      const threadId = String(payload?.meta?.threadId || '').trim();

      return threadId && state?.workflowDraftReadFailedThreadId === threadId ? { ...state, workflowDraftReadFailedThreadId: null } : state;
    },

    GET_THREAD_WORKFLOW_DRAFT_FAIL: (state, payload) => {
      const threadId = String(payload?.meta?.threadId || '').trim();

      return threadId ? { ...state, workflowDraftReadFailedThreadId: threadId } : state;
    },

    GET_WORKFLOW_TEST_SETUP_SUCCESS: (state, payload) => {
      const workflowId = String(payload?.meta?.workflowId || '').trim();

      if (!workflowId) {
        return state;
      }

      return {
        ...state,
        workflowTestSetupByWorkflowId: { ...(state?.workflowTestSetupByWorkflowId || {}), [workflowId]: payload?.data || null },
        workflowTestSavedByWorkflowId: { ...(state?.workflowTestSavedByWorkflowId || {}), [workflowId]: newerWorkflowTest(state?.workflowTestSavedByWorkflowId?.[workflowId], payload?.data?.saved) }
      };
    },

    // The window's own save landed: it is the stored test now, and further edits build on its revision.
    PUT_WORKFLOW_TEST_SUCCESS: (state, payload) => {
      const workflowId = String(payload?.meta?.workflowId || '').trim();
      const test = payload?.data?.test || null;
      const modal = state?.modals?.automationWorkflowRunLogs || {};

      if (!workflowId || !test) {
        return state;
      }

      return {
        ...state,
        workflowTestSavedByWorkflowId: { ...(state?.workflowTestSavedByWorkflowId || {}), [workflowId]: newerWorkflowTest(state?.workflowTestSavedByWorkflowId?.[workflowId], test) },
        modals: modal.workflowId === workflowId && modal.testChoices !== null && modal.testChoices !== undefined ? { ...(state?.modals || {}), automationWorkflowRunLogs: { ...modal, testBaseRevision: test.revision } } : state?.modals
      };
    },

    /* The save was refused because the test changed since the edits began (Pieve prepared one, or another window
       saved): the window shows the stored test and says so, rather than overwriting it. Any other failure keeps the
       edits and says the test isn't saved. */
    PUT_WORKFLOW_TEST_FAIL: (state, payload) => {
      const workflowId = String(payload?.meta?.workflowId || '').trim();
      const modal = state?.modals?.automationWorkflowRunLogs || {};
      const isChanged = payload?.code === 'WORKFLOW_TEST_REVISION_CHANGED';

      if (!workflowId || modal.workflowId !== workflowId) {
        return state;
      }

      return {
        ...state,
        workflowTestSavedByWorkflowId: isChanged ? { ...(state?.workflowTestSavedByWorkflowId || {}), [workflowId]: newerWorkflowTest(state?.workflowTestSavedByWorkflowId?.[workflowId], payload?.data?.test) } : state?.workflowTestSavedByWorkflowId,
        modals: { ...(state?.modals || {}), automationWorkflowRunLogs: isChanged ? { ...modal, testChoices: null, testBaseRevision: null, testNotice: 'changed' } : { ...modal, testNotice: 'saveFailed' } }
      };
    },

    // Pieve prepared a test (workflow.test.updated): it replaces the test, the window's unsaved edits included.
    uiSetWorkflowTestSaved: (state, payload) => {
      const workflowId = payload?.workflowId;
      const modal = state?.modals?.automationWorkflowRunLogs || {};

      if (!workflowId) {
        return state;
      }

      return {
        ...state,
        workflowTestSavedByWorkflowId: { ...(state?.workflowTestSavedByWorkflowId || {}), [workflowId]: newerWorkflowTest(state?.workflowTestSavedByWorkflowId?.[workflowId], payload.test) },
        modals: modal.workflowId === workflowId && modal.testChoices !== null && modal.testChoices !== undefined ? { ...(state?.modals || {}), automationWorkflowRunLogs: { ...modal, testChoices: null, testBaseRevision: null, testNotice: 'replaced' } } : state?.modals
      };
    },

    // The environment the person picked for a workflow's Test and Run now, when its source step declares more
    // than one. Not persisted: a pick is per sitting, and a workflow with one environment never reads it.
    uiSetWorkflowRunEnvironment: (state, payload) => {
      return payload?.workflowId ? { ...state, workflowRunEnvironmentIdByWorkflowId: { ...(state?.workflowRunEnvironmentIdByWorkflowId || {}), [payload.workflowId]: payload.environmentId } } : state;
    },

    uiToggleModelPickerHowItWorks: (state) => {
      return { ...state, modelPickerHowItWorksOpen: !state.modelPickerHowItWorksOpen };
    },

    /* The raw list failure, stamped with the query it answered for; a successful list clears it. */
    /* One `step` frame. The first frame names every stage up front (`stageOrder`), so the stepper can
       draw the whole ladder before the first rung settles. */
    uiOpenArtifactSlider: (state, payload) => {
      const selectedKey = String(payload?.selectedKey || '').trim() || null;

      if (!selectedKey) {
        return state;
      }

      if (state?.artifactSlider?.open === true && state?.artifactSlider?.selectedKey === selectedKey) {
        return state;
      }

      return {
        ...state,
        artifactSlider: {
          ...(state?.artifactSlider || {}),
          open: true,
          selectedKey
        }
      };
    },

    uiCloseArtifactSlider: (state) => {
      if (!state?.artifactSlider?.open && !state?.artifactSlider?.selectedKey) {
        return state;
      }

      return {
        ...state,
        artifactSlider: {
          ...(state?.artifactSlider || {}),
          open: false,
          selectedKey: null
        }
      };
    },

    GET_PREFS_SUCCESS: (state, payload) => {
      const serverPrefs = payload?.data?.prefs || {};
      const nextPrefsServerPayload = serverPrefs && typeof serverPrefs === 'object' ? serverPrefs : {};

      return {
        ...state,
        prefs: applyPrefsPendingMutations({
          prefs: nextPrefsServerPayload,
          pendingMutations: state?.prefsPendingMutations
        }),
        prefsRequestStatus: 'success',
        prefsServerPayload: nextPrefsServerPayload
      };
    },

    GET_PREFS_FAIL: (state) => {
      return {
        ...state,
        prefsRequestStatus: 'failed'
      };
    },

    SET_PREF_OPTIMISTIC: (state, payload) => {
      const nextMutation = normalizePrefsPendingMutation(payload);

      if (!nextMutation) {
        return state;
      }

      const currentPendingMutations = Array.isArray(state?.prefsPendingMutations) ? state.prefsPendingMutations : [];
      const nextPendingMutations = [...currentPendingMutations.filter((item) => String(item?.requestId || '').trim() !== nextMutation.requestId), nextMutation];
      const nextPrefsServerPayload = state?.prefsServerPayload && typeof state.prefsServerPayload === 'object' ? state.prefsServerPayload : state?.prefs || {};

      return {
        ...state,
        prefs: applyPrefsPendingMutations({
          prefs: nextPrefsServerPayload,
          pendingMutations: nextPendingMutations
        }),
        prefsPendingMutations: nextPendingMutations
      };
    },

    SET_PREF_SUCCESS: (state, payload) => {
      const serverPrefs = payload?.data?.prefs;
      const requestId = String(payload?.requestId || payload?.meta?.requestId || '').trim();

      if (!serverPrefs || typeof serverPrefs !== 'object') {
        return state;
      }

      const currentPendingMutations = Array.isArray(state?.prefsPendingMutations) ? state.prefsPendingMutations : [];
      const nextPendingMutations = requestId ? currentPendingMutations.filter((item) => String(item?.requestId || '').trim() !== requestId) : currentPendingMutations;

      return {
        ...state,
        prefs: applyPrefsPendingMutations({
          prefs: serverPrefs,
          pendingMutations: nextPendingMutations
        }),
        prefsPendingMutations: nextPendingMutations,
        prefsServerPayload: serverPrefs
      };
    },

    SET_PREF_FAIL: (state, payload) => {
      const requestId = String(payload?.requestId || payload?.meta?.requestId || '').trim();

      if (!requestId) {
        return state;
      }

      const currentPendingMutations = Array.isArray(state?.prefsPendingMutations) ? state.prefsPendingMutations : [];
      const nextPendingMutations = currentPendingMutations.filter((item) => String(item?.requestId || '').trim() !== requestId);
      const nextPrefsServerPayload = state?.prefsServerPayload && typeof state.prefsServerPayload === 'object' ? state.prefsServerPayload : {};

      return {
        ...state,
        prefs: applyPrefsPendingMutations({
          prefs: nextPrefsServerPayload,
          pendingMutations: nextPendingMutations
        }),
        prefsPendingMutations: nextPendingMutations
      };
    }
  },
  {
    key: 'pieve-ui',
    whitelist: ['prefs']
  }
);
